// src/components/media/UploadSheet.tsx — the upload step. Every new file shows under its thumbnail a label picker
// ("which photo is what") and an optional caption before anything is saved; tick several and give them one label at
// once. Then each file goes up on its own with its own progress bar (docs/media-library.md).
import { useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, AlertTriangle, FileText, Film, Plus, X } from 'lucide-react';
import { Button, Input, Sheet } from '../ui';
import { LabelPicker } from './LabelPicker';
import { errorText } from './toast';
import { checkUpload, formatBytes, mediaKindOf, uploadFile, MEDIA_ACCEPT, UNLABELLED_FOLDERS, type FolderKind, type MediaItem } from '../../lib/media';

interface Row {
  key: string; file: File; preview: string | null; label: string; caption: string; picked: boolean;
  status: 'ready' | 'uploading' | 'done' | 'error'; progress: number; error: string | null; refused: string | null;
}
let seq = 0;
const toRow = (file: File): Row => {
  const refused = checkUpload(file);
  const img = mediaKindOf(file.type, file.name) === 'IMAGE' && !/\.(heic|heif)$/i.test(file.name) && !refused;
  return { key: `u${++seq}`, file, preview: img ? URL.createObjectURL(file) : null, label: '', caption: '', picked: false, status: 'ready', progress: 0, error: null, refused };
};
const PARALLEL = 3;

export function UploadSheet({ open, folder, files, onClose, onUploaded }: {
  open: boolean; folder: { id: string; name: string; kind: FolderKind }; files: File[];
  onClose: () => void;
  /** Called once the batch ends, with what was saved (the grid refreshes from it). */
  onUploaded: (items: MediaItem[], failed: number) => void;
}) {
  const [rows, setRows] = useState<Row[]>([]);
  const [bulk, setBulk] = useState('');
  const [running, setRunning] = useState(false);
  const more = useRef<HTMLInputElement>(null);
  const aborts = useRef(new Map<string, () => void>());
  const labelled = !UNLABELLED_FOLDERS.has(folder.kind);

  // A new batch of files: fresh rows (and the old previews released).
  const [batch, setBatch] = useState<File[] | null>(null);
  if (open && batch !== files) { setBatch(files); setRows(files.map(toRow)); setBulk(''); setRunning(false); }
  if (!open && batch) setBatch(null);
  // Previews are released when the sheet closes (not on every row change: they are still on screen then).
  const previews = useRef(new Set<string>());
  useEffect(() => { rows.forEach((r) => { if (r.preview) previews.current.add(r.preview); }); }, [rows]);
  useEffect(() => {
    if (open) return;
    const all = previews.current;
    all.forEach((u) => URL.revokeObjectURL(u)); all.clear();
  }, [open]);

  const patch = (key: string, p: Partial<Row>) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...p } : r)));
  const sendable = rows.filter((r) => !r.refused && r.status !== 'done');
  const picked = rows.filter((r) => r.picked && !r.refused);
  const allPicked = sendable.length > 0 && sendable.every((r) => r.picked);
  const done = rows.filter((r) => r.status === 'done').length;
  const failed = rows.filter((r) => r.status === 'error').length;
  const unlabelledImages = labelled ? rows.filter((r) => !r.refused && !r.label && mediaKindOf(r.file.type, r.file.name) === 'IMAGE').length : 0;
  const total = useMemo(() => sendable.reduce((n, r) => n + r.file.size, 0), [sendable]);

  const start = async () => {
    const queue = rows.filter((r) => !r.refused && r.status !== 'done');
    if (!queue.length) return;
    setRunning(true);
    const saved: MediaItem[] = []; let errors = 0;
    const work = async () => {
      for (let r = queue.shift(); r; r = queue.shift()) {
        const row = r;
        patch(row.key, { status: 'uploading', progress: 0, error: null });
        const up = uploadFile(folder.id, row.file, { label: labelled ? row.label || null : null, caption: row.caption.trim() || null }, (f) => patch(row.key, { progress: f }));
        aborts.current.set(row.key, up.abort);
        try { saved.push(...(await up.promise)); patch(row.key, { status: 'done', progress: 1 }); }
        catch (e) { errors++; patch(row.key, { status: 'error', error: errorText(e) }); }
        finally { aborts.current.delete(row.key); }
      }
    };
    await Promise.all(Array.from({ length: Math.min(PARALLEL, queue.length) }, work));
    setRunning(false);
    onUploaded(saved, errors);
    if (!errors) onClose();
  };

  const close = () => {
    if (running) { aborts.current.forEach((a) => a()); return; }
    onClose();
  };

  return (
    <Sheet open={open} onClose={close} size="xl" eyebrow="Upload" title={`Add to ${folder.name}`}
      subtitle={labelled ? 'Say what each photo shows — pick from the list or type your own. Tick several to label them together.' : 'Floorplans and EPCs are labelled by their folder.'}
      footer={<>
        <span className="mr-auto text-[12px] text-ink-500">
          {running ? `Uploading ${done} of ${sendable.length + done}…` : failed ? `${failed} failed — fix or remove them and try again` : `${sendable.length} file${sendable.length === 1 ? '' : 's'} · ${formatBytes(total)}${unlabelledImages ? ` · ${unlabelledImages} without a label` : ''}`}
        </span>
        <Button variant="secondary" onClick={close}>{running ? 'Cancel uploads' : 'Cancel'}</Button>
        <Button icon="Upload" disabled={running || !sendable.length} onClick={() => void start()}>{failed ? 'Try again' : `Upload ${sendable.length || ''}`.trim()}</Button>
      </>}
      below={labelled && rows.length > 1 ? (
        <div className="flex flex-wrap items-center gap-2 border-b border-ink-100 bg-ink-50/60 px-4 py-2.5 sm:px-5">
          <label className="inline-flex items-center gap-2 text-[12.5px] text-ink-700">
            <input type="checkbox" className="h-4 w-4 accent-[var(--color-bronze-500)]" checked={allPicked} disabled={running}
              onChange={(e) => setRows((rs) => rs.map((r) => (r.refused || r.status === 'done' ? r : { ...r, picked: e.target.checked })))} />
            Select all
          </label>
          <span className="text-[12px] text-ink-400">{picked.length ? `${picked.length} selected` : 'Tick photos to label them together'}</span>
          <div className="ml-auto flex items-center gap-2">
            <LabelPicker value={bulk} onChange={setBulk} disabled={running} className="w-56" placeholder="Label for the selected" />
            <Button size="sm" variant="secondary" disabled={running || !picked.length || !bulk}
              onClick={() => setRows((rs) => rs.map((r) => (r.picked && !r.refused ? { ...r, label: bulk } : r)))}>Apply to {picked.length || 'selected'}</Button>
          </div>
        </div>
      ) : undefined}>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {rows.map((r) => {
          const kind = mediaKindOf(r.file.type, r.file.name);
          return (
            <div key={r.key} className={`overflow-hidden rounded-xl border bg-white ${r.picked ? 'border-bronze-500 ring-2 ring-bronze-500/25' : r.refused || r.status === 'error' ? 'border-red-200' : 'border-ink-200'}`}>
              <div className="relative aspect-[4/3] bg-ink-50">
                {r.preview ? <img src={r.preview} alt="" className="h-full w-full object-cover" />
                  : <div className="grid h-full w-full place-items-center text-ink-400">{kind === 'PDF' ? <FileText className="h-8 w-8" strokeWidth={1.5} /> : kind === 'VIDEO' ? <Film className="h-8 w-8" strokeWidth={1.5} /> : <AlertTriangle className="h-7 w-7" />}</div>}
                {labelled && !r.refused && r.status !== 'done' && (
                  <label className="absolute left-2 top-2 grid h-6 w-6 cursor-pointer place-items-center rounded-md bg-white/90 shadow-sm">
                    <input type="checkbox" aria-label={`Select ${r.file.name}`} className="h-4 w-4 accent-[var(--color-bronze-500)]" checked={r.picked} disabled={running} onChange={(e) => patch(r.key, { picked: e.target.checked })} />
                  </label>
                )}
                {!running && r.status !== 'done' && (
                  <button type="button" aria-label={`Remove ${r.file.name}`} onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))}
                    className="absolute right-2 top-2 grid h-6 w-6 place-items-center rounded-full bg-white/90 text-ink-500 shadow-sm hover:text-ink-900"><X className="h-3.5 w-3.5" /></button>
                )}
                {r.status === 'done' && <span className="absolute right-2 top-2 grid h-6 w-6 place-items-center rounded-full bg-white text-emerald-600 shadow-sm"><CheckCircle2 className="h-4 w-4" /></span>}
                {(r.status === 'uploading' || r.status === 'done') && (
                  <div className="absolute inset-x-0 bottom-0 h-1.5 bg-ink-900/10" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(r.progress * 100)}>
                    <div className="h-full bg-bronze-500 transition-[width]" style={{ width: `${Math.round(r.progress * 100)}%` }} />
                  </div>
                )}
              </div>
              <div className="space-y-1.5 p-2">
                <p className="truncate text-[11.5px] text-ink-500" title={r.file.name}>{r.file.name} · {formatBytes(r.file.size)}{r.status === 'uploading' ? ` · ${Math.round(r.progress * 100)}%` : ''}</p>
                {r.refused ? <p className="text-[11.5px] text-red-600">{r.refused}</p> : <>
                  {labelled && <LabelPicker size="sm" value={r.label} onChange={(label) => patch(r.key, { label })} disabled={running || r.status === 'done'} />}
                  <Input value={r.caption} onChange={(e) => patch(r.key, { caption: e.target.value })} disabled={running || r.status === 'done'} maxLength={300} placeholder="Caption (optional)" className="h-7 w-full text-[12px]" />
                  {r.error && <p className="text-[11.5px] text-red-600">{r.error}</p>}
                </>}
              </div>
            </div>
          );
        })}
        {!running && (
          <button type="button" onClick={() => more.current?.click()}
            className="grid aspect-[4/3] place-items-center rounded-xl border border-dashed border-ink-300 text-[12.5px] font-medium text-ink-500 transition hover:border-bronze-500 hover:text-bronze-600">
            <span className="flex flex-col items-center gap-1"><Plus className="h-5 w-5" />Add more</span>
          </button>
        )}
      </div>
      <input ref={more} type="file" multiple accept={MEDIA_ACCEPT} className="hidden"
        onChange={(e) => { const list = Array.from(e.target.files ?? []); e.target.value = ''; if (list.length) setRows((rs) => [...rs.filter((r) => r.status !== 'done'), ...list.map(toRow)]); }} />
    </Sheet>
  );
}
